import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { di } from 'react-magnetic-di';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useGlobalObjectPreviewPanels as usePlatformGlobalObjectPreviewPanels,
	usePreviewPanelIFrameCommunication,
	asGlobalObjectType,
	type GlobalObjectType,
} from '@atlassian/preview-panel-global-objects';
import {
	PreviewPanelsProvider as PlatformPreviewPanelsProvider,
	usePreviewPanelsActions,
} from '@atlassian/preview-panels-api';

type OpenPreviewPanel = (props: {
	url: string;
	ari: string;
	name: string;
	iconUrl?: string;
}) => void;
type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;
type EventType = 'open' | 'close';
type NotifyCallback = (event: EventType) => void;

const SUPPORT_PANEL_TYPES_MILESTONE_ONE: GlobalObjectType[] = [
	'identity-team',
	'confluence-page',
	'confluence-whiteboard',
	'townsquare-goal',
	'townsquare-project',
];

const JiraPreviewPanelsContext = createContext<{
	subscribe: (callback: (event: 'open' | 'close') => void) => () => void;
	openPreviewPanel: OpenPreviewPanel;
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
} | null>(null);

/**
 * Only return the route name and path if the route is preview panel enabled.
 * Return as a string to ensure the value is stable and avoid unnecessary re-renders.
 */
export const useEnabledRoute = createRouterSelector<string | null>(({ route, location }) =>
	route.supportsPreviewPanel ? `${route.name}:${location.pathname}` : null,
);

const JiraPreviewPanelsProvider = ({
	children,
	subscribe,
}: {
	children: ReactNode;
	subscribe: (callback: NotifyCallback) => () => void;
}) => {
	di(window);

	const { close } = usePreviewPanelsActions();
	const enabledRoute = useEnabledRoute();
	const isIssueEmbed = enabledRoute?.split(':')[0] === 'issue-embed';

	let supportedObjects: GlobalObjectType[] | undefined = fg('jira-glance-issue-panel')
		? undefined
		: SUPPORT_PANEL_TYPES_MILESTONE_ONE;

	// If the route is an issue embed, we can get the supported objects from the URL search params.
	// This allows us to support custom objects in the preview panel for the host application.
	if (isIssueEmbed && typeof window !== 'undefined') {
		const urlSearchParams = new URLSearchParams(window.location.search);
		supportedObjects = urlSearchParams.get('previewPanels')?.split(',')?.map(asGlobalObjectType);
	}

	const { isPreviewPanelAvailable, openPreviewPanel } = usePlatformGlobalObjectPreviewPanels(
		'jira',
		{
			supportedObjects,
			enabled: enabledRoute !== null,
		},
	);

	usePreviewPanelIFrameCommunication(openPreviewPanel);

	useEffect(
		() => () => {
			// automatically close the preview panel when the route changes
			close();
		},
		[enabledRoute, close],
	);

	const value = useMemo(
		() => ({
			subscribe,
			openPreviewPanel,
			isPreviewPanelAvailable,
		}),
		[subscribe, openPreviewPanel, isPreviewPanelAvailable],
	);

	return (
		<JiraPreviewPanelsContext.Provider value={value}>{children}</JiraPreviewPanelsContext.Provider>
	);
};

export const PreviewPanelsProvider = ({ children }: { children: ReactNode }) => {
	const subscribers = useRef<Set<NotifyCallback>>(new Set());

	const notifySubscribers = useCallback((event: EventType) => {
		subscribers.current.forEach((callback) => callback(event));
	}, []);

	const handlePreviewPanelOpen = useCallback(() => notifySubscribers('open'), [notifySubscribers]);

	const handlePreviewPanelClose = useCallback(
		() => notifySubscribers('close'),
		[notifySubscribers],
	);

	const subscribe = useCallback((callback: NotifyCallback) => {
		subscribers.current.add(callback);

		return () => {
			subscribers.current.delete(callback);
		};
	}, []);

	return (
		<PlatformPreviewPanelsProvider
			onPreviewPanelOpen={handlePreviewPanelOpen}
			onPreviewPanelClose={handlePreviewPanelClose}
		>
			<JiraPreviewPanelsProvider subscribe={subscribe}>{children}</JiraPreviewPanelsProvider>
		</PlatformPreviewPanelsProvider>
	);
};

export const useGlobalObjectPreviewPanels = () => {
	const context = useContext(JiraPreviewPanelsContext);
	if (!context) {
		throw new Error('useGlobalObjectPreviewPanels must be used within a PreviewPanelsProvider');
	}

	const { openPreviewPanel, isPreviewPanelAvailable } = context;

	return {
		openPreviewPanel,
		isPreviewPanelAvailable,
	};
};

export const usePreviewPanelStateChanged = (callback: NotifyCallback) => {
	const { subscribe } = useContext(JiraPreviewPanelsContext) ?? {};

	useEffect(() => {
		if (!subscribe) return;

		const unsubscribe = subscribe(callback);
		return () => unsubscribe();
	}, [subscribe, callback]);
};
